import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
    Link2, Loader2, RefreshCw, Wifi, Copy, MapPin, CheckCircle2, XCircle,
    ArrowDownLeft, ArrowUpRight, FileText, Download,
} from "lucide-react";

const fmtAt = (iso) => {
    try {
        return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Rome" });
    } catch { return "—"; }
};

const contractText = (origin, key) => `INTEGRAZIONE HR ⇄ RLS — CONTRATTO API (chiave condivisa nell'header X-API-Key)

CHIAVE CONDIVISA: ${key}
URL HR: ${origin}

FORMATI DATI (valgono in entrambe le direzioni):
- Date: GG/MM/AAAA (es. 15/06/2026)
- Ore: con la virgola e due decimali (es. "1,50")
- Orari: HH:MM (es. 18:00)
- Dipendenti: SOLO matricola e nominativo (niente email né qualifica)

A) ENDPOINT CHE L'HR ESPONE (l'app RLS li chiama con header X-API-Key):

1. GET ${origin}/api/integrations/employees
   → {count, employees:[{nome, cognome, matricola, active}]}

2. GET ${origin}/api/integrations/worksites
   → {count, worksites:[{hr_id, name, client, address, active, needs_review, rls_id}]}

3. POST ${origin}/api/integrations/worksites
   Body: {rls_id, name, client_name?, address?}
   Quando in RLS si crea/modifica un cliente-cantiere. L'HR geocodifica
   automaticamente l'indirizzo e segna il cantiere "da verificare".
   → {id, result: created|updated, geocoded, needs_review}

4. POST ${origin}/api/integrations/extra-reports
   Body: {rls_id, matricola, date: "GG/MM/AAAA", hours: "1,50", description,
          time_from?, time_to?, worksite_name?, note?}
   Quando in RLS viene completato un rapporto di lavoro straordinario.
   In HR entra già CONFERMATO. Idempotente su rls_id (re-invio = aggiornamento).
   → {id, result: created|updated} — 422 se la matricola non esiste in HR.

5. GET ${origin}/api/integrations/planned-jobs?date_from=01/06/2026&date_to=30/06/2026
   → {count, jobs:[{hr_id, matricola, dipendente, date, hours, time_from,
      time_to, description, worksite_name, status}]}
   (pull di riserva dei lavori programmati dall'ufficio)

B) ENDPOINT CHE L'APP RLS DEVE ESPORRE (l'HR li chiama con la stessa chiave):

1. GET  {RLS}/api/integrations/hr/ping → {ok: true}

2. POST {RLS}/api/integrations/hr/employees
   Body: {employees:[{matricola, nome, cognome, active}]}
   Upsert per matricola. active=false ⇒ disattivare il dipendente in RLS.
   Chiamato in tempo reale a ogni creazione/modifica/cessazione e nel
   riallineamento manuale (tutto l'organico in un'unica chiamata).

3. POST {RLS}/api/integrations/hr/worksites
   Body: {worksites:[{hr_id, name, client, address, active}]}
   Upsert per hr_id (o per nome se hr_id sconosciuto). active=false ⇒ disattivare.

4. POST {RLS}/api/integrations/hr/planned-jobs
   Body: {jobs:[{hr_id, matricola, dipendente, date: "GG/MM/AAAA", hours: "1,50",
          time_from, time_to, description, worksite_name, status}]}
   status: "assegnata" = nuovo lavoro programmato dall'ufficio HR (da mostrare
   in RLS come lavoro pianificato); "annullata" = rimuovere/annullare.
   Upsert per hr_id.

5. GET  {RLS}/api/integrations/hr/worksites
   → {worksites:[{rls_id, name, client_name?, address?}]}
   Deve restituire TUTTI i clienti-cantieri presenti in RLS: l'HR lo chiama
   quando l'ufficio preme "Importa clienti da RLS". In più, a ogni nuovo
   cliente creato/modificato in RLS, l'app RLS deve chiamare
   POST ${origin}/api/integrations/worksites (vedi A.3) per il push in tempo reale.

Tutte le risposte 2xx sono considerate OK. Autenticazione: rifiutare le
richieste senza header X-API-Key corretto (401).`;

export default function RlsIntegrationPage() {
    const [status, setStatus] = useState(null);
    const [log, setLog] = useState([]);
    const [pinging, setPinging] = useState(false);
    const [pingResult, setPingResult] = useState(null);
    const [syncing, setSyncing] = useState(false);
    const [syncResult, setSyncResult] = useState(null);
    const [importing, setImporting] = useState(false);
    const [importResult, setImportResult] = useState(null);
    const [showContract, setShowContract] = useState(false);

    const load = useCallback(async () => {
        try {
            const [s, l] = await Promise.all([api.get("/rls/status"), api.get("/rls/log")]);
            setStatus(s.data);
            setLog(l.data);
        } catch { toast.error("Errore nel caricamento (funzione riservata all'amministratore)"); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const doPing = async () => {
        setPinging(true);
        setPingResult(null);
        try {
            const { data } = await api.post("/rls/ping");
            setPingResult(data);
            if (data.ok) toast.success("Connessione con RLS riuscita");
            else toast.error("RLS non raggiungibile: " + (data.error || ""));
        } catch { toast.error("Errore nel test"); }
        finally { setPinging(false); load(); }
    };

    const doSync = async () => {
        setSyncing(true);
        setSyncResult(null);
        try {
            const { data } = await api.post("/rls/sync-all");
            setSyncResult(data);
            const allOk = data.employees?.ok && data.worksites?.ok && data.planned_jobs?.ok;
            if (allOk) toast.success("Sincronizzazione completata");
            else toast.error("Sincronizzazione con errori: controlla il log");
        } catch { toast.error("Errore nella sincronizzazione"); }
        finally { setSyncing(false); load(); }
    };

    const doImport = async () => {
        setImporting(true);
        setImportResult(null);
        try {
            const { data } = await api.post("/rls/import-worksites");
            setImportResult(data);
            if (data.ok) toast.success(`Importati da RLS: ${data.created} nuovi, ${data.updated} aggiornati`);
            else toast.error("Import non riuscito: " + (data.error || ""));
        } catch { toast.error("Errore nell'import da RLS"); }
        finally { setImporting(false); load(); }
    };

    const copyText = (text, msg) => {
        navigator.clipboard.writeText(text).then(() => toast.success(msg)).catch(() => toast.error("Copia non riuscita"));
    };

    if (!status) {
        return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div>;
    }

    const origin = window.location.origin;

    return (
        <div className="mx-auto max-w-5xl px-6 py-8" data-testid="rls-page">
            <div className="mb-6 flex items-start gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-md bg-[#376DB3]/10 text-[#376DB3]">
                    <Link2 className="h-6 w-6" />
                </div>
                <div>
                    <h1 className="font-display text-2xl font-bold text-slate-800">App Straordinari (RLS)</h1>
                    <p className="text-sm text-slate-500">Comunicazione bidirezionale: dipendenti, clienti-cantieri e lavori straordinari</p>
                </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
                <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm" data-testid="rls-status-card">
                    <h2 className="font-display text-sm font-bold uppercase tracking-wide text-slate-600">Stato collegamento</h2>
                    <dl className="mt-3 space-y-2 text-sm">
                        <div className="flex justify-between"><dt className="text-slate-500">App RLS</dt>
                            <dd className="font-mono-numbers text-xs font-semibold text-slate-700">{status.base_url || "non configurata"}</dd></div>
                        <div className="flex justify-between"><dt className="text-slate-500">Configurazione</dt>
                            <dd>{status.configured
                                ? <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-bold text-emerald-700">COMPLETA</span>
                                : <span className="rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-bold text-red-600">MANCANTE</span>}</dd></div>
                        <div className="flex justify-between"><dt className="text-slate-500">Push automatico in tempo reale</dt>
                            <dd data-testid="rls-enabled-state">{status.enabled
                                ? <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-bold text-emerald-700">ATTIVO</span>
                                : <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold text-slate-500">DISATTIVO</span>}</dd></div>
                        <div className="flex justify-between"><dt className="text-slate-500">Ultima sincronizzazione OK</dt>
                            <dd className="text-xs text-slate-700">{status.last_ok_sync ? fmtAt(status.last_ok_sync) : "mai"}</dd></div>
                    </dl>
                    <div className="mt-4 flex flex-wrap gap-2">
                        <Button variant="outline" size="sm" className="gap-1.5 text-xs" onClick={doPing} disabled={pinging} data-testid="rls-ping-btn">
                            {pinging ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wifi className="h-3.5 w-3.5" />} Prova connessione
                        </Button>
                        <Button size="sm" className="gap-1.5 bg-[#376DB3] text-xs text-white hover:bg-[#2c5a96]" onClick={doSync} disabled={syncing} data-testid="rls-sync-btn">
                            {syncing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Sincronizza tutto ora
                        </Button>
                        <Button variant="outline" size="sm" className="gap-1.5 text-xs" onClick={doImport} disabled={importing} data-testid="rls-import-worksites-btn">
                            {importing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} Importa clienti da RLS
                        </Button>
                    </div>
                    {pingResult && (
                        <div className={`mt-3 rounded-md px-3 py-2 text-xs font-medium ${pingResult.ok ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"}`} data-testid="rls-ping-result">
                            {pingResult.ok ? "✓ L'app RLS risponde correttamente" : `✗ ${pingResult.error || "Non raggiungibile"} — probabilmente gli endpoint non sono ancora stati creati in RLS`}
                        </div>
                    )}
                    {importResult && (
                        <div className={`mt-3 rounded-md px-3 py-2 text-xs font-medium ${importResult.ok ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"}`} data-testid="rls-import-result">
                            {importResult.ok
                                ? `✓ Ricevuti ${importResult.total} clienti da RLS: ${importResult.created} nuovi, ${importResult.updated} aggiornati${importResult.created > 0 ? " — controlla coordinate e raggio nella pagina Cantieri" : ""}`
                                : `✗ ${importResult.error || "Import non riuscito"}`}
                        </div>
                    )}
                    {syncResult && (
                        <div className="mt-3 space-y-1 rounded-md bg-slate-50 px-3 py-2 text-xs" data-testid="rls-sync-result">
                            {["employees", "worksites", "planned_jobs"].map((k) => (
                                <div key={k} className="flex items-center justify-between">
                                    <span className="text-slate-500">{k === "employees" ? "Dipendenti" : k === "worksites" ? "Clienti/Cantieri" : "Lavori programmati"} ({syncResult[k]?.count ?? 0})</span>
                                    {syncResult[k]?.ok
                                        ? <span className="inline-flex items-center gap-1 font-bold text-emerald-600"><CheckCircle2 className="h-3.5 w-3.5" /> inviati</span>
                                        : <span className="inline-flex items-center gap-1 font-bold text-red-500" title={syncResult[k]?.error}><XCircle className="h-3.5 w-3.5" /> errore</span>}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm" data-testid="rls-key-card">
                    <h2 className="font-display text-sm font-bold uppercase tracking-wide text-slate-600">Chiave condivisa (X-API-Key)</h2>
                    <p className="mt-2 text-xs text-slate-500">Questa chiave va configurata anche nell'app RLS: autentica le chiamate in entrambe le direzioni.</p>
                    <div className="mt-3 flex items-center gap-2">
                        <code className="flex-1 truncate rounded-md bg-slate-100 px-2.5 py-2 font-mono text-xs text-slate-700" data-testid="rls-key-value">{status.inbound_key || "—"}</code>
                        <Button variant="outline" size="icon" className="h-8 w-8 shrink-0" onClick={() => copyText(status.inbound_key, "Chiave copiata")} data-testid="rls-key-copy">
                            <Copy className="h-3.5 w-3.5" />
                        </Button>
                    </div>
                    {status.worksites_to_review > 0 && (
                        <Link to="/presenze/cantieri" className="mt-4 flex items-center gap-2 rounded-md bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-700 hover:bg-amber-100" data-testid="rls-review-link">
                            <MapPin className="h-4 w-4" /> {status.worksites_to_review} cantieri arrivati da RLS da verificare (coordinate/raggio)
                        </Link>
                    )}
                    <button type="button" onClick={() => setShowContract((v) => !v)}
                        className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-[#376DB3] hover:underline" data-testid="rls-contract-toggle">
                        <FileText className="h-3.5 w-3.5" /> {showContract ? "Nascondi" : "Mostra"} il contratto API da dare all'app RLS
                    </button>
                </div>
            </div>

            {showContract && (
                <div className="mt-4 rounded-lg border border-slate-200 bg-white p-5 shadow-sm" data-testid="rls-contract-card">
                    <div className="flex items-center justify-between">
                        <h2 className="font-display text-sm font-bold uppercase tracking-wide text-slate-600">Contratto API — da incollare nella chat dell'app RLS</h2>
                        <Button variant="outline" size="sm" className="gap-1.5 text-xs" onClick={() => copyText(contractText(origin, status.inbound_key), "Contratto copiato negli appunti")} data-testid="rls-contract-copy">
                            <Copy className="h-3.5 w-3.5" /> Copia tutto
                        </Button>
                    </div>
                    <pre className="mt-3 max-h-96 overflow-auto whitespace-pre-wrap rounded-md bg-slate-900 p-4 text-[11px] leading-relaxed text-slate-100">{contractText(origin, status.inbound_key)}</pre>
                </div>
            )}

            <div className="mt-6 rounded-lg border border-slate-200 bg-white shadow-sm">
                <div className="border-b border-slate-100 px-4 py-3 font-display text-sm font-bold uppercase tracking-wide text-slate-600">Log sincronizzazioni</div>
                {log.length === 0 ? (
                    <div className="p-8 text-center text-sm text-slate-500" data-testid="rls-log-empty">Nessuna attività registrata.</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm" data-testid="rls-log-table">
                            <thead className="bg-slate-50 text-[11px] font-bold uppercase tracking-wide text-slate-500">
                                <tr>
                                    <th className="px-3 py-2.5">Quando</th>
                                    <th className="px-3 py-2.5">Direzione</th>
                                    <th className="px-3 py-2.5">Entità</th>
                                    <th className="px-3 py-2.5">Azione</th>
                                    <th className="px-3 py-2.5">N.</th>
                                    <th className="px-3 py-2.5">Esito</th>
                                    <th className="px-3 py-2.5">Dettaglio</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {log.map((l) => (
                                    <tr key={l.id} className="hover:bg-slate-50/60">
                                        <td className="whitespace-nowrap px-3 py-2 font-mono-numbers text-xs text-slate-600">{fmtAt(l.at)}</td>
                                        <td className="px-3 py-2">
                                            {l.direction === "in"
                                                ? <span className="inline-flex items-center gap-1 rounded bg-sky-100 px-1.5 py-0.5 text-[10px] font-bold text-sky-700"><ArrowDownLeft className="h-3 w-3" /> DA RLS</span>
                                                : <span className="inline-flex items-center gap-1 rounded bg-violet-100 px-1.5 py-0.5 text-[10px] font-bold text-violet-700"><ArrowUpRight className="h-3 w-3" /> VERSO RLS</span>}
                                        </td>
                                        <td className="px-3 py-2 text-xs capitalize text-slate-700">{l.entity}</td>
                                        <td className="px-3 py-2 text-xs text-slate-600">{l.action}</td>
                                        <td className="px-3 py-2 font-mono-numbers text-xs text-slate-600">{l.count ?? 1}</td>
                                        <td className="px-3 py-2">
                                            {l.ok
                                                ? <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-bold text-emerald-700">OK</span>
                                                : <span className="rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-bold text-red-600">ERRORE</span>}
                                        </td>
                                        <td className="max-w-[260px] truncate px-3 py-2 text-xs text-slate-500" title={l.error || l.detail || ""}>{l.error || l.detail || "—"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
